import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { combineLatest, map, Subject, takeUntil } from 'rxjs';
import { Grade } from '../../../../../models/grade.school.model';
import { Group } from '../../../../../models/group.model';
import { AppState } from '../../../../../state/appstate';
import * as gradeSelector from '../../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../../state/selectors/group.selector';

@Component({
  selector: 'app-academic-status',
  standalone: true,
  imports: [BaseChartDirective],
  templateUrl: './academic-status.component.html',
  styleUrl: './academic-status.component.css',
})
export class AcademicStatusComponent implements OnInit, OnDestroy {
  route: ActivatedRoute = inject(ActivatedRoute);
  @ViewChild(BaseChartDirective) chart: BaseChartDirective<'bar'> | undefined;

  store: Store<AppState> = inject(Store);

  grades: Grade[] | null | undefined;
  group: Group | null | undefined;

  groupId: string | null = null;

  // Same chart styling as the dashboard's yearly charts; status colours kept
  public barChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        align: 'start',
        labels: { color: '#52514e', boxWidth: 12, boxHeight: 12 },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { color: '#52514e', autoSkip: false },
        border: { display: false },
      },
      y: {
        stacked: true,
        grid: { color: '#e8e7e3' },
        ticks: { color: '#52514e', precision: 0 },
        border: { display: false },
      },
    },
  };
  public barChartType: ChartType = 'bar';
  public barChartLegend = true;
  public barChartPlugins = [];

  public barChartData: ChartData<'bar'> = {
    labels: [],
    datasets: [
      {
        stack: 'a',
        data: [],
        label: 'Completed',
        backgroundColor: '#12AA52',
        borderColor: '#ffffff',
        borderWidth: 1,
        maxBarThickness: 40,
      },
      {
        stack: 'a',
        data: [],
        label: 'Open',
        backgroundColor: '#cccdd2',
        borderColor: '#ffffff',
        borderWidth: 1,
        borderRadius: 4, // top segment carries the rounded end
        borderSkipped: 'start',
        maxBarThickness: 40,
      },
    ],
  };

  exit$ = new Subject<void>();

  private params$ = combineLatest({
    params: this.route.paramMap,
  }).pipe(
    map(({ params }) => {
      // If current params are empty, try to look at the parent
      let pMap = params;
      let currentRoute = this.route;

      // Walk up the tree until we find the groupId or hit the top
      while (currentRoute.parent && !pMap.has('id')) {
        currentRoute = currentRoute.parent;
        pMap = currentRoute.snapshot.paramMap;
      }

      return {
        groupId: pMap.get('id'),
      };
    }),
    takeUntil(this.exit$),
  );

  constructor() {
    this.params$.subscribe(({ groupId }) => {
      this.groupId = groupId;
      this.store.select(gradeSelector.getGradesByGroup(groupId));
    });
  }

  ngOnInit(): void {
    this.store
      .select(gradeSelector.getGradesByGroup(this.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((grades) => {
        this.grades = grades;
        this.setChartData();
      });

    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
        this.setChartData();
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  setChartData() {
    if (this.group?.lessonStats && this.grades) {
      this.barChartData.labels = [];
      this.barChartData.datasets[0].data = [];
      this.barChartData.datasets[1].data = [];
      this.grades.forEach((grade) => {
        this.barChartData.labels?.push(grade.gradeName);
        this.barChartData.datasets[0].data.push(
          this.group?.lessonStats![grade.id!]?.closed ?? 0,
        );
        this.barChartData.datasets[1].data.push(
          (this.group?.lessonStats![grade.id!]?.total ?? 0) -
            (this.group?.lessonStats![grade.id!]?.closed ?? 0),
        );
      });

      // console.log(`Chart Data: ${JSON.stringify(this.barChartData, null, 2)}`);
      this.chart?.update();
    }
  }
}
